"use client";

import { useCallback, useEffect, useRef, useState } from "react";

// Generic keyset/cursor pagination + infinite-scroll trigger, reusable for
// any "list of items" panel (chats, agents, etc). Callers supply a
// `fetchPage` that already applies their table's filter/order/cursor — this
// hook only owns list state, the loading-more guard, and the
// IntersectionObserver that fires it near the bottom of the scroll area.

export type UseCursorPaginationOptions<TItem, TCursor> = {
  pageSize: number;
  fetchPage: (cursor: TCursor | null, pageSize: number) => Promise<TItem[]>;
  getCursor: (item: TItem) => TCursor;
  getKey: (item: TItem) => string;
  /** Change this to force a reload from the first page (e.g. a changed filter). */
  resetKey?: unknown;
};

export type UseCursorPaginationResult<TItem> = {
  items: TItem[];
  loading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  /** Attach to a sentinel element at the bottom of the list. */
  sentinelRef: (node: Element | null) => void;
  /** Attach to the scrollable ancestor the sentinel is observed against. */
  rootRef: (node: HTMLDivElement | null) => void;
  reload: () => void;
  removeItem: (key: string) => void;
  patchItem: (key: string, patch: Partial<TItem>) => void;
};

export function useCursorPagination<TItem, TCursor>({
  pageSize,
  fetchPage,
  getCursor,
  getKey,
  resetKey,
}: UseCursorPaginationOptions<TItem, TCursor>): UseCursorPaginationResult<TItem> {
  const [items, setItems] = useState<TItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const isFetchingRef = useRef(false);
  const cursorRef = useRef<TCursor | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const rootNodeRef = useRef<HTMLDivElement | null>(null);
  // The observer only calls back on a threshold *crossing*. If the sentinel
  // is already inside the trigger margin on mount, scrolling further doesn't
  // cross anything new, so it never fires again — track the live state
  // ourselves and re-check it from the scroll handler too.
  const isIntersectingRef = useRef(false);
  const hasScrolledRef = useRef(false);

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const getCursorRef = useRef(getCursor);
  getCursorRef.current = getCursor;
  const getKeyRef = useRef(getKey);
  getKeyRef.current = getKey;

  const loadFirstPage = useCallback(async () => {
    isFetchingRef.current = true;
    setLoading(true);
    cursorRef.current = null;
    try {
      const page = await fetchPageRef.current(null, pageSize);
      setItems(page);
      cursorRef.current = page.length > 0 ? getCursorRef.current(page[page.length - 1]) : null;
      setHasMore(page.length === pageSize);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [pageSize]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage, resetKey]);

  const loadMore = useCallback(async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    setIsFetchingMore(true);
    try {
      const page = await fetchPageRef.current(cursorRef.current, pageSize);
      setItems((prev) => {
        const seen = new Set(prev.map((item) => getKeyRef.current(item)));
        const fresh = page.filter((item) => !seen.has(getKeyRef.current(item)));
        return [...prev, ...fresh];
      });
      if (page.length > 0) cursorRef.current = getCursorRef.current(page[page.length - 1]);
      setHasMore(page.length === pageSize);
    } finally {
      setIsFetchingMore(false);
      isFetchingRef.current = false;
    }
  }, [hasMore, pageSize]);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  // Fires a load when the sentinel is (still) in view: either the container
  // hasn't overflowed yet (backfilling to make it scrollable at all), or the
  // user has genuinely scrolled since it became scrollable.
  const maybeLoadMore = useCallback(() => {
    if (!isIntersectingRef.current) return;
    const root = rootNodeRef.current;
    const containerOverflows = root ? root.scrollHeight > root.clientHeight + 1 : false;
    if (containerOverflows && !hasScrolledRef.current) return;
    void loadMoreRef.current();
  }, []);

  const maybeLoadMoreRef = useRef(maybeLoadMore);
  maybeLoadMoreRef.current = maybeLoadMore;

  const handleScroll = useCallback(() => {
    hasScrolledRef.current = true;
    maybeLoadMoreRef.current();
  }, []);

  // Attaches once per scroll-container DOM node; tracks real scroll activity
  // so the sentinel below can't auto-trigger a load before the user scrolls.
  const rootRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootNodeRef.current?.removeEventListener("scroll", handleScroll);
      rootNodeRef.current = node;
      node?.addEventListener("scroll", handleScroll, { passive: true });
    },
    [handleScroll]
  );

  // Attaches once per sentinel DOM node (stable identity) and always calls
  // through to the latest loadMore via the ref above.
  const sentinelRef = useCallback((node: Element | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        isIntersectingRef.current = entries[0]?.isIntersecting ?? false;
        maybeLoadMoreRef.current();
      },
      { root: rootNodeRef.current, rootMargin: "200px" }
    );
    observerRef.current.observe(node);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const removeItem = useCallback((key: string) => {
    setItems((prev) => prev.filter((item) => getKeyRef.current(item) !== key));
  }, []);

  const patchItem = useCallback((key: string, patch: Partial<TItem>) => {
    setItems((prev) =>
      prev.map((item) => (getKeyRef.current(item) === key ? { ...item, ...patch } : item))
    );
  }, []);

  return {
    items,
    loading,
    isFetchingMore,
    hasMore,
    sentinelRef,
    rootRef,
    reload: loadFirstPage,
    removeItem,
    patchItem,
  };
}
